import {MouseEvent, useState} from 'react';
import {Button, Group, Modal, Stack, Text, Textarea} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

// FNP-1714: the Write-off action for a FAILED BillableEvent. Presentational: it owns only
// the dialog and the reason text, and hands the trimmed reason to onConfirm, which does the
// mutation. Confirm stays disabled while the reason is empty or whitespace; billing-api
// refuses a blank reason too (BillableEventWriteOffReasonRequired).
//
// The table row navigates to the detail page on click. React bubbles events from a portal
// through the component tree, so a click on the modal (overlay, input, buttons) would reach
// the row as well. The wrapping span stops every one of them.
export const WriteOffButton = ({onConfirm}: { onConfirm: (reason: string) => Promise<unknown> }) => {
    const [opened, setOpened] = useState(false);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const trimmed = reason.trim();

    const close = () => {
        setOpened(false);
        setReason('');
    };

    const handleConfirm = async () => {
        if (trimmed === '') {
            return;
        }
        setSubmitting(true);
        try {
            await onConfirm(trimmed);
            close();
        } catch {
            // onConfirm has already shown the error. Keep the dialog and the typed reason so
            // the admin can retry without retyping it.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <span onClick={(e: MouseEvent) => e.stopPropagation()}>
            <Button size="xs" color="red" variant="outline" onClick={() => setOpened(true)}>
                {I18n.get('Write off')}
            </Button>
            <Modal opened={opened} onClose={close} title={I18n.get('Write off this billable event?')}>
                <Stack gap="md">
                    <Text size="sm">
                        {I18n.get('This is irreversible. The event will never be billed and cannot be requeued. The original failure reason is kept.')}
                    </Text>
                    <Textarea
                        label={I18n.get('Reason')}
                        description={I18n.get('Why this event can never be billed. Recorded on the event with your name.')}
                        required
                        data-autofocus
                        autosize
                        minRows={2}
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={close}>
                            {I18n.get('Cancel')}
                        </Button>
                        <Button color="red" disabled={trimmed === ''} loading={submitting} onClick={handleConfirm}>
                            {I18n.get('Write off permanently')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </span>
    );
};
